import type { VerticalCardData } from '@/app/components/shared/cards/VerticalCard'
import type { ProductApiItem } from '@/app/lib/api/types-api'
import { formatEventDate } from '@/app/lib/dates'
import { filterVisiblePromotions, parsePriceField, resolveEventPrice } from '@/app/lib/pricing'
import { resolvePurchaseState } from '@/app/lib/purchaseState'
import { route } from '@/app/lib/routing'
import type { PriceDisplay, PurchaseState } from '@/app/lib/types'
import { resolveCardImage, resolveCardSticker, resolveProductGenre } from '@/app/lib/utils'

/** Igual que `VerticalCardData`, pero con `priceDisplay` garantizado: este
 * mapper siempre lo rellena (a diferencia de postventa, que no muestra
 * precio) — evita el chequeo de undefined en cada consumidor/test. */
type ProductCardData = VerticalCardData & { readonly priceDisplay: PriceDisplay }

/** Ciudad del evento (sin el nombre del recinto, a petición). */
function formatCity(venue: ProductApiItem['venue']): string {
  return venue?.city ?? ''
}

/**
 * Algunos productos reales de `/data` traen la imagen elegida (ver
 * `resolveCardImage`) con el protocolo duplicado (`https://https://dominio/...`),
 * un defecto de datos del backend: `next/image` interpreta entonces `"https"`
 * como hostname y falla. Se corrige el duplicado y, si aun así la URL no es
 * válida o está vacía, se omite (renderizado defensivo — `VerticalCard` no
 * pinta imagen).
 *
 * Además se recortan las comillas (codificadas o literales) pegadas al FINAL de
 * la URL —`…images_16-9.png%22`, otro defecto de datos visto en el payload real
 * del módulo `ProductosDestacados`—. Ojo: `new URL()` NO las descarta, porque son
 * caracteres válidos en un path, así que sin este recorte pasarían el filtro y
 * `next/image` pediría un recurso inexistente
 * (specs/023-productos-destacados-module, research §3.1).
 */
function sanitizeImageUrl(url: string): string {
  const deduped = url
    .replace(/^(https?:\/\/)(?=https?:\/\/)/, '')
    .replace(/(?:%22|%27|["'])+$/i, '')
  try {
    new URL(deduped)
    return deduped
  } catch {
    return ''
  }
}

/** Estado de compra del producto, en un único sitio: lo consumen tanto el mapper
 * como `isLinkableProduct`, y ambos deben coincidir. */
function purchaseStateOf(product: ProductApiItem): PurchaseState {
  return resolvePurchaseState({
    isCancelled: product.is_cancelled,
    purchaseText: product.purchase_text,
    disablePurchase: product.disable_purchase,
    soldOut: product.sold_out,
    saleEnd: product.sale_end,
    saleStart: product.sale_start,
  })
}

/**
 * Destino de la tarjeta: SIEMPRE la ficha del producto (PDP), nunca
 * `purchase_url`. Con género, la PDP es `[slug]/[slug2]` con el género como
 * primer segmento — omitirlo no daría una URL más corta sino otra ruta
 * distinta (`[slug]` = home por localización/categoría; antes esto producía
 * `/entradas//entradas-…/`, con doble barra). Sin género no hay ficha
 * derivable y la tarjeta no tiene destino: caer a `purchase_url` metería al
 * visitante en la pasarela de compra con solo pulsar la card (título/cuerpo,
 * el *stretched link* de `VerticalCard`), no el botón de compra explícito —
 * confundiendo "ver la ficha" con "comprar" (mismo criterio que
 * `Productos3Columnas/utils/toGridCards.ts`, que nunca tuvo este fallback).
 *
 * Cadena vacía ⇒ producto sin destino derivable: el consumidor DEBE descartarlo
 * (`isLinkableProduct`), nunca pintar la tarjeta con `href=""`.
 */
function resolveCardHref(product: ProductApiItem): string {
  const genreSlug = resolveProductGenre(product.genres)?.nicename ?? ''
  return genreSlug ? route(`/${genreSlug}/${product.url}`) : ''
}

/**
 * `true` si la tarjeta de este producto puede llevar a la PDP. Todos los
 * listados que reciban `ProductApiItem[]` deben filtrar con esto antes de
 * pintar, para no renderizar una tarjeta sin destino derivable — p. ej. sin
 * `genres` (specs/023-productos-destacados-module, research §4).
 */
export function isLinkableProduct(product: ProductApiItem): boolean {
  return resolveCardHref(product) !== ''
}

/**
 * `ProductApiItem` → `VerticalCardData`: etiqueta desde `style_name` del
 * género resuelto por `resolveProductGenre` (fallback a `name`, ausente si no
 * hay género); el estado de compra
 * (tag superpuesta a la imagen, texto/habilitación del CTA) lo resuelve
 * `resolvePurchaseState` en un único orden de precedencia
 * (specs/019-estados-compra-eventos). Genérico y reutilizable por cualquier
 * página que liste productos de `/data` con `VerticalCard` (no solo "Lo más
 * vendido") — mismo motivo que `sanitizeAppliedFilters` en este mismo
 * directorio.
 *
 * `preferVerticalImage`: para tarjetas de póster (4:5), donde la imagen
 * cuadrada, que es la elegida por defecto, se recorta por los laterales; la
 * vertical encaja sin perder información.
 */
export function mapProductToCard(
  product: ProductApiItem,
  options: { readonly preferVerticalImage?: boolean } = {},
): ProductCardData {
  const genre = resolveProductGenre(product.genres)
  const tag = genre?.style_name ?? genre?.name ?? undefined

  const visiblePromotions = filterVisiblePromotions(product.promotions ?? [])
  const priceDisplay = resolveEventPrice({
    minPrice: product.min_price,
    originalPrice: product.original_price,
    visiblePromotions,
    hasAppPromotions: product.has_app_promotions,
    salePrice: parsePriceField(product.sale_price),
    discountPercentage: parsePriceField(product.discount),
  })

  const purchaseState = purchaseStateOf(product)
  const isPurchasable = purchaseState.kind === 'default'

  // El botón de la CARD muestra "Próximamente" en vez del texto largo
  // ("Disponible el <fecha>") — la tag sobre la imagen sí conserva el texto
  // largo, igual que la PDP (contracts/resolvePurchaseState.md, Nota).
  const ctaLabel = purchaseState.kind === 'upcoming' ? 'Próximamente' : purchaseState.ctaLabel

  return {
    productId: product.id,
    title: product.title,
    href: resolveCardHref(product),
    img: sanitizeImageUrl(
      options.preferVerticalImage
        ? resolveCardImage(product.image_vertical, product.image_square)
        : resolveCardImage(product.image_square, product.image_vertical),
    ),
    date: formatEventDate({
      start: product.start_at,
      end: product.end_at,
      datesType: product.dates_type,
      dateNotVisible: product.date_not_visible,
    }),
    venue: formatCity(product.venue),
    priceDisplay,
    tag,
    sticker: resolveCardSticker(product.stickers),
    ctaLabel,
    imageBadge: purchaseState.imageBadge ?? undefined,
    ctaHref: isPurchasable ? (product.purchase_url ?? undefined) : undefined,
    ctaDisabled: !purchaseState.ctaEnabled,
  }
}
